import React, { useState } from "react"

import { format } from "date-fns"
import { ArrowDown, ArrowUp, Minus } from "lucide-react"

import { DAY_LABELS, DAY_NAMES, DAY_SHORT_LABELS } from "@/lib/collectionCoverage"
import { formatDayNumeric } from "@/lib/dateFormats"
import { formatCost } from "@/lib/routeCostCalculation"
import { cn } from "@/lib/utils"
import { DaySummary, WeekData } from "@/lib/weeklyPlan"

import DeltaInsightSheet, { DeltaInsightType } from "./DeltaInsightSheet"
import { COMPACT_DAY_PX, COMPACT_LEFT_PX, COMPACT_TABLE_MIN_WIDTH } from "./compactColumns"

interface DeltaStripProps {
  leftData: WeekData
  rightData: WeekData
  leftWeekDays: Date[]
  rightWeekDays: Date[]
}

const DeltaStrip: React.FC<DeltaStripProps> = ({
  leftData,
  rightData,
  leftWeekDays,
  rightWeekDays,
}) => {
  const [insight, setInsight] = useState<{
    type: DeltaInsightType
    dayIndex: number
    dayLabel: string
  } | null>(null)
  const leftDailySummaries = leftData.dailySummaries
  const rightDailySummaries = rightData.dailySummaries

  const rows: {
    key: string
    label: string
    getLeft: (ds: DaySummary) => number
    getRight: (ds: DaySummary) => number
    isCost: boolean
    clickable: boolean
  }[] = [
    {
      key: "routes",
      label: "Routes",
      getLeft: (ds) => ds.routeCount,
      getRight: (ds) => ds.routeCount,
      isCost: false,
      clickable: true,
    },
    {
      key: "labour",
      label: "Labour",
      getLeft: (ds) => ds.labourCost,
      getRight: (ds) => ds.labourCost,
      isCost: true,
      clickable: false,
    },
    {
      key: "charging",
      label: "Charging",
      getLeft: (ds) => ds.chargingCost,
      getRight: (ds) => ds.chargingCost,
      isCost: true,
      clickable: false,
    },
    {
      key: "haulage",
      label: "Haulage Revenue",
      getLeft: (ds) => ds.haulageRevenue,
      getRight: (ds) => ds.haulageRevenue,
      isCost: true,
      clickable: true,
    },
    {
      key: "vehicle",
      label: "Vehicle",
      getLeft: (ds) => ds.vehicleCost,
      getRight: (ds) => ds.vehicleCost,
      isCost: true,
      clickable: false,
    },
    {
      key: "total",
      label: "Total",
      getLeft: (ds) => ds.totalCost - ds.haulageRevenue,
      getRight: (ds) => ds.totalCost - ds.haulageRevenue,
      isCost: true,
      clickable: false,
    },
  ]

  const renderDelta = (leftVal: number, rightVal: number, isCost: boolean) => {
    const delta = rightVal - leftVal
    const pct = leftVal !== 0 ? (delta / Math.abs(leftVal)) * 100 : 0
    const isUp = delta > 0
    const costBetter = isCost ? delta < 0 : isUp
    const color =
      delta === 0 ? "text-palette-jungleGreen500" : costBetter ? "text-green-600" : "text-red-600"
    const Icon = delta === 0 ? Minus : isUp ? ArrowUp : ArrowDown
    return (
      <div className={cn("flex items-center gap-0.5 justify-center", color)}>
        <Icon className="h-3 w-3 flex-shrink-0" />
        <span className="text-xs font-semibold tabular-nums">
          {isCost ? formatCost(Math.abs(delta)) : Math.abs(delta)}
          {leftVal !== 0 && (
            <span className="text-[10px] opacity-90">
              {" "}
              ({pct >= 0 ? "+" : ""}
              {pct.toFixed(0)}%)
            </span>
          )}
        </span>
      </div>
    )
  }

  const deltaColgroup = (
    <colgroup>
      <col style={{ width: COMPACT_LEFT_PX, minWidth: COMPACT_LEFT_PX }} />
      {[0, 1, 2, 3, 4, 5, 6].map((i) => (
        <col key={i} style={{ width: COMPACT_DAY_PX, minWidth: COMPACT_DAY_PX }} />
      ))}
      <col style={{ width: COMPACT_DAY_PX, minWidth: COMPACT_DAY_PX }} />
    </colgroup>
  )

  return (
    <>
      <div className="rounded-lg border border-palette-jungleGreen200 overflow-hidden bg-white">
        <table
          className="w-full border-collapse text-sm"
          style={{ tableLayout: "fixed", minWidth: COMPACT_TABLE_MIN_WIDTH }}
        >
          {deltaColgroup}
          <thead className="sticky top-0 z-20">
            <tr className="bg-palette-jungleGreen800 text-white">
              <th
                className="sticky left-0 z-30 bg-palette-jungleGreen800 px-3 py-2 text-left text-xs font-semibold"
                style={{ width: COMPACT_LEFT_PX, minWidth: COMPACT_LEFT_PX }}
              >
                Week-on-Week Change
              </th>
              {leftWeekDays.map((_, i) => (
                <th key={i} className="px-3 py-2 text-center text-xs font-semibold">
                  {DAY_SHORT_LABELS[DAY_NAMES[i]]}
                </th>
              ))}
              <th className="px-3 py-2 text-center text-xs font-bold bg-palette-jungleGreen900">
                Week
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const leftWeekTot = leftWeekDays.reduce((s, d) => {
                const ds = leftDailySummaries[format(d, "yyyy-MM-dd")]
                return s + (ds ? row.getLeft(ds) : 0)
              }, 0)
              const rightWeekTot = rightWeekDays.reduce((s, d) => {
                const ds = rightDailySummaries[format(d, "yyyy-MM-dd")]
                return s + (ds ? row.getRight(ds) : 0)
              }, 0)
              const isTotalRow = row.key === "total"
              return (
                <tr
                  key={row.key}
                  className={cn(
                    "border-b border-palette-jungleGreen100",
                    isTotalRow
                      ? "bg-blue-50 border-t-2 border-blue-300"
                      : "bg-palette-jungleGreen000 hover:bg-palette-jungleGreen100/80",
                  )}
                >
                  <td
                    className={cn(
                      "sticky left-0 z-10 px-3 py-2 text-xs font-semibold border-r border-palette-jungleGreen200",
                      isTotalRow
                        ? "bg-blue-50 text-blue-900 py-3.5 text-sm font-bold"
                        : "bg-inherit text-palette-jungleGreen700 py-3",
                    )}
                  >
                    {row.label}
                  </td>
                  {leftWeekDays.map((day, i) => {
                    const leftDs = leftDailySummaries[format(day, "yyyy-MM-dd")]
                    const rightDs = rightDailySummaries[format(rightWeekDays[i], "yyyy-MM-dd")]
                    const l = leftDs ? row.getLeft(leftDs) : 0
                    const r = rightDs ? row.getRight(rightDs) : 0
                    const clickable = row.clickable
                    return (
                      <td
                        key={i}
                        className={cn(
                          "px-3 py-2 text-center border-r border-palette-jungleGreen200 tabular-nums",
                          isTotalRow ? "bg-blue-50 text-blue-900 font-bold py-3.5" : "bg-inherit",
                          clickable && "cursor-pointer hover:bg-blue-50 transition-colors",
                        )}
                        onClick={
                          clickable
                            ? () =>
                                setInsight({
                                  type: row.key === "routes" ? "routes" : "revenue",
                                  dayIndex: i,
                                  dayLabel: `${DAY_LABELS[DAY_NAMES[i]]} ${formatDayNumeric(day)}`,
                                })
                            : undefined
                        }
                      >
                        {renderDelta(l, r, row.isCost)}
                      </td>
                    )
                  })}
                  <td
                    className={cn(
                      "px-3 py-2 text-center tabular-nums",
                      isTotalRow
                        ? "bg-blue-100 text-blue-900 font-bold py-3.5"
                        : "bg-palette-jungleGreen200",
                      row.clickable && "cursor-pointer hover:bg-blue-50",
                    )}
                    onClick={
                      row.clickable
                        ? () =>
                            setInsight({
                              type: row.key === "routes" ? "routes" : "revenue",
                              dayIndex: -1,
                              dayLabel: "Week",
                            })
                        : undefined
                    }
                  >
                    {renderDelta(leftWeekTot, rightWeekTot, row.isCost)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <DeltaInsightSheet
        open={insight !== null}
        onClose={() => setInsight(null)}
        type={insight?.type ?? "routes"}
        dayIndex={insight?.dayIndex ?? 0}
        dayLabel={insight?.dayLabel ?? ""}
        leftData={leftData}
        rightData={rightData}
        leftWeekDays={leftWeekDays}
        rightWeekDays={rightWeekDays}
      />
    </>
  )
}

export default DeltaStrip
